import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

import { CircularProgress } from "@mui/material";
import AddOnNotes from "./addOnNotes";
import DictationMessage from "components/modals/dictationMessage";
import { useEffect } from "react";
import { useState } from "react";
import { useUserData } from "components/customHooks/userDataContext";
import { getDictationMessages } from "apiHandler/actions";

const drawerWidth = 400;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: "-100px",
    }),
  }),
);

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

export default function PersistentDrawerLeft({
  html,
  isHtmlLoading,
  MedCodes,
  PersonalInfo,
  PatientNotes,
  PatientDetails,
  EncounterInfo,
  details,
  setDetails,
  medCodes,
  suggDiag,
  setSuggDiag,
  suggProce,
  setSuggProce,
  loading,
  setLoading,
  setEncounterId,
  medCodeError,
  readOnly,
  location,
  encounter_id,
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const userData = useUserData();
  const token = userData?.access_token;
  const currentUserEmail = userData?.email;
  const handleDrawer = () => {
    setOpen(!open);
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const payload = {
        type: "Scribe",
        search_criteria: {
          scribe_id: currentUserEmail,
          dictation_id: details.id,
        },
      };
      const result = await getDictationMessages(payload, token);

      // Transform API response - use email match for isUser
      const formattedMessages = result
        .map((msg) => ({
          id: msg.id,
          text: msg.message,
          isUser: msg.user === currentUserEmail, // ✅ Our message if user email matches
          created_date: msg.created_date,
          user: msg.user,
          user_type: msg.user_type,
        }))
        .sort((a, b) => new Date(a.created_date) - new Date(b.created_date)); // Sort by date

      setMessages(formattedMessages);
    } catch (error) {
      console.log("Error fetching messages: ", error);
      setMessages([]);
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <div className="drawer-container">
        <Toolbar about="Open Transcription Text">
          <Typography
            sx={{
              mr: 0,
              m: 2,
              mt: 5,
              cursor: "pointer",
              ...(open && { display: "none" }),
            }}
            onClick={handleDrawer}
            color="primary"
          >
            {"<<"} Show Transcription Text
          </Typography>
        </Toolbar>

        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
            },
          }}
          variant="persistent"
          anchor="left"
          open={open}
        >
          <DrawerHeader>
            <IconButton onClick={handleDrawer}>
              {theme.direction === "ltr" ? (
                <ChevronLeftIcon />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          </DrawerHeader>
          <Divider />
          {isHtmlLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexGrow: 1,
              }}
            >
              <CircularProgress color="#fff" size={24} />
            </div>
          ) : `${html}`.length ? (
            <div
              style={{ paddingLeft: 12, paddingRight: 12 }}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            <div style={{ marginTop: "auto", marginBottom: "auto" }}>
              <Alert severity="error">
                <AlertTitle>Error</AlertTitle>
                <strong>
                  Unable to retrieve the response from the Server!
                </strong>
              </Alert>
            </div>
          )}
        </Drawer>
      </div>
      <Main open={open}>
        <Typography paragraph>
          <PersonalInfo
            provider_name={location.state.provider_name}
            details={details}
            encounter_id={encounter_id}
            setEncounterId={setEncounterId}
            setDetails={setDetails}
            readOnly={readOnly}
            practice_id={details.practice_id}
            npi={details.npi}
            id={details.id}
            setLoading={setLoading}
          />
          <PatientDetails
            dictation_id={details.id}
            locationData={location.state}
          />
          <EncounterInfo
            provider_name={location.state.provider_name}
            provider_first_name={details.provider_first_name}
            provider_last_name={details.provider_last_name}
            dos={details.dos}
            dictation_id={details.id}
            chief_complaint={details.chief_complaint}
            location={details.location}
            practice_id={details.practice_id}
            npi={details.npi}
            encounter_id={encounter_id}
            appt_type={details.appt_type}
            setEncounterId={setEncounterId}
            readOnly={readOnly}
            details={details}
          />
          <PatientNotes
            dictation_id={details.id}
            practice_id={details.practice_id}
            npi={details.npi}
            encounter_id={encounter_id}
            setSuggDiag={setSuggDiag}
            setSuggProce={setSuggProce}
            setLoading={setLoading}
            setEncounterId={setEncounterId}
            readOnly={readOnly}
            details={details}
          />

          <MedCodes
            medCodes={medCodes}
            suggDiag={suggDiag}
            suggProce={suggProce}
            loading={loading}
            details={details}
            medCodeError={medCodeError}
            readOnly={readOnly}
          />
          <AddOnNotes
            dictation_id={details.id}
            practice_id={details.practice_id}
            npi={details.npi}
          />
          <DictationMessage
            dictation_id={details.id}
            messages={messages}
            setMessages={setMessages}
          />
        </Typography>
      </Main>
    </Box>
  );
}
